import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { AppShell } from '@/components/AppShell';
import { useFeed } from '@/contexts/FeedContext';
import { POPULAR_RELAYS, isWebsocketUrl, normalizeRelayUrl } from '@/lib/jumble';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Radio, Plus, Trash2, Check, ArrowLeft } from 'lucide-react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { InfiniteFeed } from '@/components/feed';
import { useRelayFeed } from '@/hooks/useFeeds';
import { useSocial } from '@/contexts/SocialContext';
import { relayHttpOrigin } from '@/lib/jumble';

interface RelayInfoDoc {
  name?: string;
  description?: string;
  pubkey?: string;
  contact?: string;
  supported_nips?: number[];
  limitation?: { max_message_length?: number; max_subscriptions?: number };
  payments_url?: string;
  fees?: unknown;
  icon?: string;
}

function useRelayInfo(url: string) {
  return useQuery({
    queryKey: ['jumble', 'relay-info', url],
    queryFn: async ({ signal }) => {
      const origin = relayHttpOrigin(url);
      if (!origin) throw new Error('Bad relay URL');
      const res = await fetch(origin, { headers: { Accept: 'application/nostr+json' }, signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RelayInfoDoc;
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

export function RelaysPage() {
  useSeoMeta({ title: 'Explore relays — Jumble', description: 'Browse Nostr relays and dive into each relay community feed.' });
  const { relaySets, addRelaySet, removeRelaySet } = useFeed();
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = () => {
    setError(null);
    const raw = url.trim();
    if (!raw) return;
    if (!isWebsocketUrl(raw.startsWith('ws') ? raw : `wss://${raw}`)) { setError('Enter a valid relay URL (wss://…)'); return; }
    const normalized = normalizeRelayUrl(raw);
    navigate(`/relay/${encodeURIComponent(normalized)}`);
  };

  return (
    <AppShell title="Relays">
      <div className="border-b p-4">
        <h2 className="font-extrabold">Explore relay feeds</h2>
        <p className="text-sm text-muted-foreground">Every relay is its own community. Open one to read its feed — like Jumble's signature view.</p>
        <div className="mt-3 flex gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="wss://relay.example.com/" onKeyDown={(e) => e.key === 'Enter' && submit()} />
          <Button onClick={submit}><Plus className="h-4 w-4" /> Open</Button>
        </div>
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
      <div className="p-4">
        <h3 className="mb-2 text-sm font-bold text-muted-foreground uppercase tracking-wide">Popular relays</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {POPULAR_RELAYS.map((r) => (
            <RelayRow key={r.url} url={r.url} fallbackName={r.name} />
          ))}
        </div>
        <h3 className="mt-6 mb-2 text-sm font-bold text-muted-foreground uppercase tracking-wide">Your relay sets</h3>
        <div className="flex flex-col gap-2">
          {relaySets.map((s) => (
            <Card key={s.id} className="shadow-none">
              <CardContent className="flex items-center gap-2 p-3">
                <Link to={`/feed/${s.id}`} className="min-w-0 flex-1">
                  <p className="truncate font-semibold hover:underline">{s.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{s.relayUrls.length} relays</p>
                </Link>
                <Button size="sm" variant="outline" onClick={() => navigate(`/relay/${encodeURIComponent(s.relayUrls[0] ?? '')}`)} disabled={s.relayUrls.length === 0}>Open first</Button>
                {!['global', 'bitcoin', 'tech', 'art'].includes(s.id) && (
                  <Button size="icon" variant="ghost" onClick={() => removeRelaySet(s.id)} aria-label="Delete set"><Trash2 className="h-4 w-4" /></Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        <CreateSetForm onCreate={(name, urls) => addRelaySet({ id: `custom-${Date.now()}`, name, relayUrls: urls })} />
      </div>
      <div className="pb-20 md:pb-4" />
    </AppShell>
  );
}

function CreateSetForm({ onCreate }: { onCreate: (name: string, urls: string[]) => void }) {
  const [name, setName] = useState('');
  const [urls, setUrls] = useState('');
  const [done, setDone] = useState(false);
  void done;
  return (
    <Card className="mt-4 shadow-none">
      <CardContent className="flex flex-col gap-2 p-3">
        <p className="font-semibold text-sm">Create a relay set</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Set name (e.g. My community)" />
        <Input value={urls} onChange={(e) => setUrls(e.target.value)} placeholder="Relay URLs, comma separated" />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => {
              const list = urls.split(',').map((u) => u.trim()).filter(Boolean).map(normalizeRelayUrl);
              if (!name.trim() || list.length === 0) return;
              onCreate(name.trim(), list);
              setName(''); setUrls(''); setDone(true);
              setTimeout(() => setDone(false), 2000);
            }}
          >
            Create set
          </Button>
          {done && <span className="flex items-center gap-1 text-sm text-green-600"><Check className="h-4 w-4" /> Saved</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function RelayRow({ url, fallbackName }: { url: string; fallbackName: string }) {
  const info = useRelayInfo(url);
  const navigate = useNavigate();
  void navigate;
  return (
    <Card className="shadow-none transition-colors hover:bg-accent/40">
      <Link to={`/relay/${encodeURIComponent(url)}`} className="flex items-center gap-3 p-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-sky-500 to-violet-500 text-white">
          <Radio className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{info.data?.name || fallbackName}</span>
          <span className="block truncate font-mono text-xs text-muted-foreground">{url.replace('wss://', '')}</span>
          {info.data?.description && <span className="block truncate text-xs text-muted-foreground">{info.data.description}</span>}
        </span>
      </Link>
    </Card>
  );
}

export function RelayPage() {
  const { url } = useParams<{ url: string }>();
  const relayUrl = url ? decodeURIComponent(url) : '';
  const info = useRelayInfo(relayUrl);
  const { showReplies } = useSocial();
  const { setFeed } = useFeed();
  const navigate = useNavigate();
  const { nostr } = useNostr();
  void nostr;

  useSeoMeta({ title: `${info.data?.name ?? relayUrl} — Jumble relay`, description: info.data?.description ?? `Browse the ${relayUrl} relay feed.` });

  const feed = useRelayFeed(relayUrl ? [relayUrl] : [], { showReplies, enabled: !!relayUrl });

  return (
    <AppShell
      title={info.data?.name ?? relayUrl.replace('wss://', '')}
      right={<Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>}
    >
      <div className="border-b p-4">
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-sky-500 to-violet-500 text-white"><Radio className="h-6 w-6" /></span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-extrabold">{info.data?.name ?? 'Relay'}</h2>
            <p className="truncate font-mono text-xs text-muted-foreground">{relayUrl}</p>
          </div>
          <Button size="sm" onClick={() => { setFeed({ feedType: 'relay', id: relayUrl, name: info.data?.name ?? relayUrl }); navigate('/'); }}>
            Set as feed
          </Button>
        </div>
        {info.data?.description && <p className="mt-2 text-sm text-muted-foreground">{info.data.description}</p>}
        {info.data?.supported_nips && <p className="mt-1 text-xs text-muted-foreground">NIPs: {info.data.supported_nips.slice(0, 12).join(', ')}{info.data.supported_nips.length > 12 ? '…' : ''}</p>}
        {info.isError && <p className="mt-2 text-xs text-muted-foreground">No NIP-11 metadata — feed still works.</p>}
      </div>
      <InfiniteFeed
        events={feed.events}
        isLoading={feed.isLoading}
        isFetchingNextPage={feed.isFetchingNextPage}
        hasNextPage={feed.hasNextPage}
        fetchNextPage={() => feed.fetchNextPage()}
        emptyMessage="This relay returned no notes. It may be offline, paid, or empty."
      />
    </AppShell>
  );
}
